import { inject, Injectable } from '@angular/core';
import { TemplateObservable } from '@bazis/shared/classes/template-observable';
import { BazisSrvService } from '@bazis/shared/services/srv.service';
import { BehaviorSubject, merge, Observable, of, switchMap, withLatestFrom } from 'rxjs';
import { BazisEntityService } from '@bazis/shared/services/entity.service';
import { combineLatestWith, filter, map, tap } from 'rxjs/operators';
import { BazisToastService } from '@bazis/shared/services/toast.service';
import { buildFilterStr } from '@bazis/utils';
import { TranslocoService } from '@jsverse/transloco';
import { ListSettings } from '@bazis/list/models/list.types';
import { API_DOC_LANGS } from '@bazis/shared/tokens/api_doc_langs.token';

/**
 * **BazisFaqService**
 *
 * FAQ data loader and cache service.
 * Provides streams for FAQ sections, subsections, and questions with caching per language.
 * Also exposes a search settings stream used to build FAQ search lists.
 */
@Injectable({
    providedIn: 'root',
})
export class BazisFaqService {
    protected entityService = inject(BazisEntityService);

    protected translocoService = inject(TranslocoService);

    /**
     * Cache for sections list per language.
     * Shape: `{ [lang: string]: EntData[] }`.
     */
    protected sectionsCache = new TemplateObservable({});

    /**
     * Cache for subsections list per language and section id.
     * Shape: `{ [lang: string]: { [sectionId: string]: EntData[] } }`.
     */
    protected subSectionsCache = new TemplateObservable({});

    /**
     * Cache for subsections questions preview per language and subsection id.
     * Shape: `{ [lang: string]: { [subsectionId: string]: EntData[] } }`.
     */
    protected subSectionsPreviewCache = new TemplateObservable({});

    /**
     * Cache for questions list per language and subsection id.
     * Shape: `{ [lang: string]: { [subsectionId: string]: EntData[] } }`.
     */
    protected questionsCache = new TemplateObservable({});

    /** Base API_DOC_LANGS_MAP string resolved from `API_DOC_LANGS` token. */
    protected readonly apiDocLangsMap = inject(API_DOC_LANGS);

    /**
     * Current UI language stream.
     * Uses `TranslocoService.langChanges$`.
     */
    lang$: Observable<string> = this.translocoService.langChanges$;

    /**
     * Sections list stream for current language.
     *
     * Loads `faq.section` entities sorted by `order`.
     * Language is mapped using `API_DOC_LANGS_MAP`.
     *
     * Uses `sectionsCache` to avoid duplicate requests for the same language.
     *
     * @returns Observable of sections entities array.
     */
    sections$ = merge(this.lang$).pipe(
        withLatestFrom(this.sectionsCache.$),
        switchMap(([lang, cacheSections]) => {
            if (cacheSections[lang]?.length > 0) return of(cacheSections[lang]);
            return this.entityService
                .getEntityList$('faq.section', {
                    params: { sort: 'order', lang: this.apiDocLangsMap[lang] },
                    limit: 1000,
                    saveSeparateItems: true,
                })
                .pipe(
                    map((result) => result.list),
                    tap((result) => {
                        this.sectionsCache.set({
                            ...this.sectionsCache._,
                            [lang]: result,
                        });
                    }),
                );
        }),
    );

    /**
     * Internal search text input.
     * Used to build `searchListSettings$`.
     */
    protected _searchText$ = new BehaviorSubject('');

    /**
     * List settings for FAQ full-text search.
     *
     * Emits only when search text is not empty.
     * Searches both question and answer translations fields for the current language.
     *
     * @returns Observable of `ListSettings` to be used by list module/components.
     */
    searchListSettings$: Observable<ListSettings> = this._searchText$.pipe(
        filter((text) => !!text),
        combineLatestWith(this.lang$),
        map(([text, lang]) => {
            return {
                entityType: 'faq.faq_item',
                skipSchema: true,
                params: {
                    translations__question__$search: text,
                    translations__answer__$search: text,
                    translations__language_code: this.apiDocLangsMap[lang],
                },
                paramGroups: [
                    {
                        group: ['translations__question__$search', 'translations__answer__$search'],
                        groupConnector: '|',
                    },
                ],
                reflectInUrl: false,
                defaultSort: 'order',
                defaultStaticFilter: 'all',
                sortSettings: [],
                staticFilters: [],
                filters: {},
            };
        }),
    );

    /**
     * @param entityService - Entity API client used to load FAQ entities.
     * @param translocoService - Transloco service used to get current language.
     */
    /**
     * Set search text and trigger `searchListSettings$` update.
     *
     * @param text - Search query string.
     * @returns void
     */
    searchByText(text) {
        this._searchText$.next(text);
    }

    /**
     * Get subsections list for a given section id, with caching by language.
     *
     * Loads `faq.sub_section` entities filtered by `section` and sorted by `order`.
     * Language is mapped using `API_DOC_LANGS_MAP`.
     *
     * @param sectionId - FAQ section entity id.
     * @returns Observable of subsection entities array.
     */
    getSubSectionBySection$(sectionId) {
        return of(null).pipe(
            withLatestFrom(this.subSectionsCache.$),
            combineLatestWith(this.lang$),
            switchMap(([[next, cacheSubsections], lang]) => {
                if (
                    cacheSubsections[lang] &&
                    cacheSubsections[lang][sectionId] &&
                    cacheSubsections[lang][sectionId].length > 0
                )
                    return of(cacheSubsections[lang][sectionId]);
                return this.entityService
                    .getEntityList$('faq.sub_section', {
                        params: {
                            filter: buildFilterStr({ section: sectionId }),
                            sort: 'order',
                            lang: this.apiDocLangsMap[lang],
                        },
                        limit: 1000,
                        saveSeparateItems: true,
                    })
                    .pipe(
                        map((result) => result.list),
                        tap((result) => {
                            this.subSectionsCache.set({
                                ...this.subSectionsCache._,
                                [lang]: {
                                    ...this.subSectionsCache._[lang],
                                    [sectionId]: result,
                                },
                            });
                        }),
                    );
            }),
        );
    }

    /**
     * Get questions list for a given subsection id, with caching by language.
     *
     * Loads `faq.faq_item` entities filtered by `subsection` and sorted by `order`.
     * Language is mapped using `API_DOC_LANGS_MAP`.
     *
     * @param subsectionId - FAQ subsection entity id.
     * @returns Observable of FAQ item entities array.
     */
    getQuestionsBySubsection$(subsectionId) {
        return of(null).pipe(
            withLatestFrom(this.questionsCache.$),
            combineLatestWith(this.lang$),
            switchMap(([[next, cacheQuestions], lang]) => {
                if (
                    cacheQuestions[lang] &&
                    cacheQuestions[lang][subsectionId] &&
                    cacheQuestions[lang][subsectionId].length > 0
                )
                    return of(cacheQuestions[lang][subsectionId]);
                return this.entityService
                    .getEntityList$('faq.faq_item', {
                        params: {
                            filter: buildFilterStr({ subsection: subsectionId }),
                            sort: 'order',
                            lang: this.apiDocLangsMap[lang],
                        },
                        limit: 1000,
                    })
                    .pipe(
                        map((result) => result.list),
                        tap((result) => {
                            this.questionsCache.set({
                                ...this.questionsCache._,
                                [lang]: {
                                    ...this.questionsCache._[lang],
                                    [subsectionId]: result,
                                },
                            });
                        }),
                    );
            }),
        );
    }

    /**
     * Get preview list of questions for a subsection.
     *
     * Uses a separate cache (`subSectionsPreviewCache`) because it can be limited.
     *
     * @param subsectionId - FAQ subsection entity id.
     * @param limit - Maximum number of items to load. Default: 5.
     * @returns Observable of FAQ item entities array.
     */
    getQuestionsPreviewBySubsection$(subsectionId, limit = 5) {
        return of(null).pipe(
            withLatestFrom(this.subSectionsPreviewCache.$),
            combineLatestWith(this.lang$),
            switchMap(([[next, cacheQuestions], lang]) => {
                if (
                    cacheQuestions[lang] &&
                    cacheQuestions[lang][subsectionId] &&
                    cacheQuestions[lang][subsectionId].length > 0
                )
                    return of(cacheQuestions[lang][subsectionId]);
                return this.entityService
                    .getEntityList$('faq.faq_item', {
                        params: {
                            filter: buildFilterStr({ subsection: subsectionId }),
                            sort: 'order',
                            lang: this.apiDocLangsMap[lang],
                        },
                        limit,
                    })
                    .pipe(
                        map((result) => result.list),
                        tap((result) => {
                            this.subSectionsPreviewCache.set({
                                ...this.subSectionsPreviewCache._,
                                [lang]: {
                                    ...this.subSectionsPreviewCache._[lang],
                                    [subsectionId]: result,
                                },
                            });
                        }),
                    );
            }),
        );
    }
}
